<template>
  <div class="customers">
    <div class="main-content-wrapper">
      <div class="page-header d-flex flex-row">
        <h1 class="m-0 flex-fill font-weight-bold">Customers</h1>
        <b-button variant="primary" @click="handleClickNewCustomer" size="lg">
          <svgicon name="plus" width="14" height="14" color="#ffffff" class="mr-2"></svgicon>
          <span>New Customer</span>
        </b-button>
      </div>
    </div>
    <div class="main-content-wrapper">
      <CustomersFilter :total-all="totalRows" />
    </div>
    <div class="main-content-wrapper table-holder">
      <CustomersTable
        :customers="customers"
        :is-loading="isLoading"
        :open-prescription-id="openPrescriptionId"
        :open-pd-id="openPdId"
        @show-prescription-modal="onShowPrescriptionModal"
        @show-prescription-edit-modal="onShowPrescriptionEditModal"
        @show-pd-modal="onShowPdModal"
        @show-pd-edit-modal="onShowPdEditModal"
        @show-prescription-copy-modal="onShowPrescriptionCopyModal"
        @show-pd-copy-modal="onShowPdCopyModal"
        @deleted="onDeleted"
        @show-pd-duplicate-modal="onShowPdDuplicateModal"
      />
    </div>
    <TablePagination ref="tablePagination" :meta="paginationMeta" :total-rows="totalRows" />
    <TablePaginationInfo :meta="paginationMeta" description="customers" />
    <ReferralCustomerModal @customer-added="onOk" />
    <NewCustomerModal @customer-added="onOk" @customer-updated="onCustomerUpdated" />
    <NewPdModal :customer="selectedCustomer" :pd="selectedPd" :isCopy="isCopy" @ok="onOk" />
    <NewPrescriptionModal
      :customer="selectedCustomer"
      :prescription="selectedPrescription"
      :isCopy="isCopy"
      @ok="onOk"
    />
    <CommentsModal />
    <FileModal />
    <DuplicatePdModal :customer="selectedCustomer" :pd="selectedPd" @ok="onOk" />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import CommentsModal from '@/components/CommentsModal.vue';
import FileModal from '@/components/modals/FileModal.vue';
import CustomersTable from '../components/CustomersTable.vue';
import NewCustomerModal from '../components/NewCustomerModal.vue';
import NewPrescriptionModal from '../components/NewPrescriptionModal.vue';
import NewPdModal from '../components/NewPdModal.vue';
import TablePagination from '../components/TablePagination.vue';
import CustomersFilter from '../components/CustomersFilter.vue';
import apiCustomers from '../api/customers';
import TablePaginationInfo from '../components/TablePaginationInfo.vue';
import ReferralCustomerModal from '../components/ReferralCustomerModal.vue';
import DuplicatePdModal from '../components/DuplicatePdModal.vue';
import '@/icons/plus';

export default {
  name: 'Customers',
  components: {
    TablePaginationInfo,
    CustomersFilter,
    TablePagination,
    NewPdModal,
    NewPrescriptionModal,
    NewCustomerModal,
    CustomersTable,
    CommentsModal,
    FileModal,
    ReferralCustomerModal,
    DuplicatePdModal,
  },
  data() {
    return {
      totalRows: 0,
      paginationMeta: {},
      selectedCustomer: {},
      selectedPrescription: null,
      selectedPd: null,
      isCopy: false,
      isLoading: false,
      openPrescriptionId: null,
      openPdId: null,
    };
  },
  computed: {
    ...mapState('customer', ['customers']),
  },
  created() {
    const query = this.getQuery();
    this.getCustomers(query);
    this.setStaffs();
    this.$root.$on('prescriptionListMutated', this.onPrescriptionSaved);
    this.$root.$on('newPdAdded', this.onPdSaved);
  },
  beforeDestroy() {
    this.$root.$off('prescriptionListMutated', this.onPrescriptionSaved);
    this.$root.$off('newPdAdded', this.onPdSaved);
  },
  methods: {
    ...mapMutations('customer', {
      setCustomers: 'SET_CUSTOMERS',
      setSelectedCustomer: 'SET_SELECTED_CUSTOMER',
      updateCustomerInCustomers: 'UPDATE_CUSTOMER_IN_CUSTOMERS',
    }),
    ...mapActions(['setStaffs']),
    getCustomers(params, showLoading = true) {
      this.isLoading = showLoading;
      const { open_pd_modal, ...apiParams } = params;
      apiCustomers
        .getCustomers(apiParams)
        .then(response => {
          const { data } = response.data;
          const { pagination } = response.data.meta;
          this.paginationMeta = response.data.meta.pagination;
          this.totalRows = pagination.total;

          const expandedIds = this.customers.filter(c => c._showDetails).map(c => c.id);
          data.forEach(c => {
            if (expandedIds.includes(c.id)) c._showDetails = true;
          });
          const targetId = parseInt(apiParams.customer_id, 10);
          const target = targetId ? data.find(c => c.id === targetId) : null;
          if (target) {
            target._showDetails = true;
          }
          this.setCustomers(data);

          if (open_pd_modal && target) {
            this.$nextTick(() => {
              this.onShowPdModal({ customer: target });
              const query = { ...this.$route.query };
              delete query.open_pd_modal;
              this.$router.replace({ query });
            });
          }
        })
        .finally(() => {
          this.isLoading = false;
          if (this.$refs.tablePagination) {
            this.$refs.tablePagination.refreshPage();
          }
        });
    },
    onShowPrescriptionEditModal(e) {
      this.isCopy = false;
      this.selectedCustomer = {
        id: e.customer.id,
        full_name: e.customer.full_name,
        isOnPageEdit: e.customer.isOnPageEdit || false,
      };
      this.selectedPrescription = e.prescription;
      this.$bvModal.show('new-prescription-modal');
    },
    onShowPdModal(e) {
      this.isCopy = false;
      this.selectedCustomer = {
        id: e.customer.id,
        full_name: e.customer.full_name,
      };
      this.selectedPd = {};
      this.$bvModal.show('new-pd-modal');
    },
    onShowPdEditModal(e) {
      this.isCopy = false;
      this.selectedCustomer = {
        id: e.customer.id,
        full_name: e.customer.full_name,
      };
      this.selectedPd = e.pd;
      this.$bvModal.show('new-pd-modal');
    },
    onShowPdDuplicateModal(e) {
      this.isCopy = false;
      this.selectedCustomer = {
        id: e.customer.id,
        full_name: e.customer.full_name,
      };
      this.selectedPd = e.pd;
      this.$bvModal.show('pd-duplicate-modal');
    },
    onShowPrescriptionCopyModal(e) {
      this.isCopy = true;
      this.selectedCustomer = {
        id: e.customer.id,
        full_name: e.customer.full_name,
      };
      this.selectedPrescription = e.prescription;
      this.$bvModal.show('new-prescription-modal');
    },
    onShowPdCopyModal(e) {
      this.isCopy = true;
      this.selectedCustomer = {
        id: e.customer.id,
        full_name: e.customer.full_name,
      };
      this.selectedPd = e.pd;
      this.$bvModal.show('new-pd-modal');
    },
    onOk() {
      // no table spinner here, it would unmount the open rows and close their cards
      this.getCustomers(this.getQuery(), false);
    },
    onPrescriptionSaved(id) {
      this.openPrescriptionId = id || null;
    },
    onPdSaved(e) {
      this.openPdId = (e && e.selectedId) || null;
    },
    onCustomerUpdated(customer) {
      const current = this.customers.find(c => c.id === customer.id);
      this.updateCustomerInCustomers({ ...current, ...customer, _showDetails: true });
    },
    onDeleted() {
      this.getCustomers(this.getQuery());
    },
    getQuery(toRoute) {
      const route = toRoute || this.$route;
      const { query } = route;
      return query;
    },
    handleClickNewCustomer() {
      this.setSelectedCustomer(null);
      this.$bvModal.show('new-customer-modal');
    },
  },
  watch: {
    $route(to) {
      const query = this.getQuery(to);
      this.getCustomers(query);
    },
  },
};
</script>
